// Which way each board direction points on screen, kept current.
//
// The measuring is `facingAngles.ts`'s and stays there; what this owns is
// *when* to ask and when the answer is worth telling React about. Both halves
// were paid for — see the comments in the effect, which came with it.

import { useEffect, useState, type RefObject } from "react";

import type { Direction } from "src/shared/board";
import type { BoardCanvasHandle } from "./BoardCanvas";
import { centreSquare, facingAngles, turnedEnough } from "./facingAngles";
import { useGame } from "./useGame";

/**
 * The screen angle of each board direction, or null before there is anything
 * to project. Callers fall back to the compass points.
 */
export function useFacingAngles(
  board: RefObject<BoardCanvasHandle | null>,
  /** @deprecated Ignored: the bounds are read off `game.board.facts()`. */
  _state?: unknown
): Record<Direction, number> | null {
  // The centre is the board's, read on the frame rather than closed over: the
  // facts are memoised on the shown board, so this is a lookup, and it means
  // a board that grows never leaves the compass measuring the old middle.
  const game = useGame();
  /**
   * Where each board direction points on screen. Measured, because a control
   * that offers a direction has to be drawn the way that direction currently
   * looks — see `facingAngles`.
   */
  const [facing, setFacing] = useState<Record<Direction, number> | null>(null);

  useEffect(() => {
    // Polled on the renderer's own frame rather than on state, because the
    // board moves without the state changing: an eased camera settles over
    // many frames after the move that started it, and a pan changes nothing
    // React can see at all.
    //
    // Throttled, and kept so: reading the projection is cheap and doing it at
    // 60Hz to answer a question a 22px arrow can barely show is not.
    let last = 0;
    const off = board.current?.onFrame?.(() => {
      const now = performance.now();
      if (now - last < 150) return;
      last = now;
      const handle = board.current;
      if (handle) {
        const centre = centreSquare(game.board.facts().bounds);
        const next = facingAngles(centre, (sq) => handle.projectCentre(sq));
        // Replaced only when it has actually turned. The board eases through
        // a rotation over many frames and this is React state — writing a
        // hair's difference every frame would re-render the shell for the
        // length of every camera move.
        setFacing((old) => (turnedEnough(old, next) ? next : old));
      }
    });
    return off;
  }, [board, game]);

  return facing;
}
